import * as THREE from "three";
import type { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";

// Quality tiers (from the superposition spike's gate 3): a tier table, how a page picks its tier (a URL value wins,
// else low on a mobile user agent and high elsewhere), and `applyQuality`, which applies a tier to a three.js
// renderer, scene and post chain at boot and per frame. The game resolves its tier once and passes it in; its own
// look code reads the tier's `fx`, `wetStreaks` and `grade` itself.
//
// Conventions applyQuality relies on: outline hulls are meshes named `*-outline`; a character root is tagged
// `userData.character = true` (or passed in `characters`); a prop that may cast when near is tagged
// `userData.castNear = true`.

export type QualityTier = "low" | "mid" | "high";

export interface QualitySettings {
  tier: QualityTier;
  /** Why this tier: the URL, the mobile user-agent default, or the desktop default. */
  source: "url" | "auto-mobile" | "default";
  /** Device pixel ratio cap (the canvas resolution). */
  pixelRatioCap: number;
  /** Canvas MSAA (a context attribute: read at renderer creation). */
  msaa: boolean;
  /** The key light's shadow map size, square. */
  shadowMapSize: number;
  /** "all": every mesh that was built to cast does; "characters": only the characters, plus any object tagged
   *  `userData.castNear = true` while it is within `nearCasterDist` of the camera. */
  shadowCasters: "all" | "characters";
  /** Metres: how near a `castNear` prop must be to cast at the "characters" setting. */
  nearCasterDist: number;
  /** Bloom: the full pass, at half its usual resolution, or off (off also drops the output pass when nothing else
   *  needs it, so the scene renders straight to the canvas). */
  bloom: "full" | "half" | "off";
  /** Metres: inverted-hull outlines (meshes named `*-outline`) are hidden beyond this camera distance. */
  outlineFar: number;
  /** General effect-quality scale for the look code: mist steps, streak counts, particle counts. */
  fx: number;
  /** Wet-road lamp streaks on or off. */
  wetStreaks: boolean;
  /** Colour grade: its own pass ("full") or folded into one cheap composite ("cheap"). */
  grade: "full" | "cheap";
  /** Largest texture side (px): bigger image textures in the scene are downscaled once, after they load. Infinity:
   *  untouched. */
  maxTextureSize: number;
}

/** One tier's settings (without its name and source). */
export type TierTable = Omit<QualitySettings, "tier" | "source">;

/** The default ladder (web-rpg-engine-stack §5): `high` changes nothing, `low` fits the mobile column. */
export const TIERS: Record<QualityTier, TierTable> = {
  high: { pixelRatioCap: 2, msaa: true, shadowMapSize: 2048, shadowCasters: "all", nearCasterDist: Infinity, bloom: "full", outlineFar: Infinity, fx: 1, wetStreaks: true, grade: "full", maxTextureSize: Infinity },
  mid: { pixelRatioCap: 1.5, msaa: true, shadowMapSize: 1024, shadowCasters: "all", nearCasterDist: Infinity, bloom: "half", outlineFar: 30, fx: 0.6, wetStreaks: true, grade: "full", maxTextureSize: 2048 },
  low: { pixelRatioCap: 1.25, msaa: true, shadowMapSize: 1024, shadowCasters: "characters", nearCasterDist: 12, bloom: "off", outlineFar: 15, fx: 0.3, wetStreaks: false, grade: "cheap", maxTextureSize: 1024 },
};

/** A phone or tablet user agent (iPadOS reports a Mac, so a touch Mac counts too). */
export function isMobileAgent(nav: Pick<Navigator, "userAgent" | "maxTouchPoints"> & { userAgentData?: { mobile?: boolean } } = navigator): boolean {
  if (nav.userAgentData?.mobile) return true;
  if (/Android|iPhone|iPad|iPod|Mobile|Silk|Kindle/i.test(nav.userAgent)) return true;
  return /Macintosh/.test(nav.userAgent) && (nav.maxTouchPoints ?? 0) > 1;
}

/** The tier for a page: `?quality=low|mid|high` in `search` wins; else low on a mobile agent, high elsewhere. */
export function resolveQuality(search: string, mobile: boolean, tiers: Record<QualityTier, TierTable> = TIERS): QualitySettings {
  const q = new URLSearchParams(search).get("quality");
  if (q === "low" || q === "mid" || q === "high") return { tier: q, source: "url", ...tiers[q] };
  if (q !== null) console.warn(`[quality] unknown ?quality=${q} (low|mid|high); using the default`);
  const tier: QualityTier = mobile ? "low" : "high";
  return { tier, source: mobile ? "auto-mobile" : "default", ...tiers[tier] };
}

/** What applyQuality did, for a perf report (live: the per-frame counts update). */
export interface QualityApplied {
  pixelRatio: number;
  bloomPasses: number;
  outputPass: boolean;
  castersOff: number;
  outlinesHidden: number;
  texturesCapped: string[];
}

export function createQualityApplied(): QualityApplied {
  return { pixelRatio: 1, bloomPasses: 0, outputPass: true, castersOff: 0, outlinesHidden: 0, texturesCapped: [] };
}

/** Downscale an image texture whose larger side exceeds `max` (drawn into a canvas; data textures are left alone).
 *  Logged to `applied.texturesCapped` when given. */
export function capTexture(t: THREE.Texture, max: number, applied?: Pick<QualityApplied, "texturesCapped">): boolean {
  const img = t.image as { width?: number; height?: number } | null;
  const w = img?.width ?? 0;
  const h = img?.height ?? 0;
  if (!w || !h || Math.max(w, h) <= max) return false;
  const drawable = (typeof ImageBitmap !== "undefined" && img instanceof ImageBitmap) || (typeof HTMLImageElement !== "undefined" && img instanceof HTMLImageElement) || (typeof HTMLCanvasElement !== "undefined" && img instanceof HTMLCanvasElement);
  if (!drawable) return false;
  const s = max / Math.max(w, h);
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(w * s));
  c.height = Math.max(1, Math.round(h * s));
  c.getContext("2d")!.drawImage(img as CanvasImageSource, 0, 0, c.width, c.height);
  t.image = c;
  t.needsUpdate = true;
  applied?.texturesCapped.push(`${t.name || t.uuid.slice(0, 8)} ${w}x${h}→${c.width}x${c.height}`);
  return true;
}

export interface QualityContext {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  composer: EffectComposer | null;
  /** The roots whose meshes keep casting at the "characters" shadow setting (a character added later tags its root
   *  `userData.character = true` instead). */
  characters: THREE.Object3D[];
  quality: QualitySettings;
  /** Where to record what was done (createQualityApplied). */
  applied: QualityApplied;
}

/**
 * Apply the tier to the boot-time renderer, scene and post chain. Call once, after the composer is built and before
 * the first frame. At `high` (TIERS) it changes nothing. Objects added later get the same caster and texture rules from
 * a scene.onBeforeRender hook (chained to any existing one), which also hides far outline hulls.
 */
export function applyQuality(ctx: QualityContext): void {
  const { renderer, scene, composer, quality: Q, applied } = ctx;

  // resolution
  const pr = Math.min(devicePixelRatio, Q.pixelRatioCap);
  if (renderer.getPixelRatio() !== pr) {
    renderer.setPixelRatio(pr);
    renderer.setSize(innerWidth, innerHeight);
  }
  applied.pixelRatio = pr;

  // the key light's shadow map (and any other shadow-casting light)
  scene.traverse((o) => {
    const l = o as THREE.DirectionalLight;
    if (!l.isLight || !l.castShadow || !l.shadow) return;
    if (l.shadow.mapSize.x === Q.shadowMapSize) return;
    l.shadow.mapSize.set(Q.shadowMapSize, Q.shadowMapSize);
    l.shadow.map?.dispose();
    (l.shadow as { map: THREE.WebGLRenderTarget | null }).map = null;
  });

  // shadow casters: only the characters (and near props, per frame below)
  const isCharacter = (o: THREE.Object3D): boolean => {
    for (let p: THREE.Object3D | null = o; p; p = p.parent) if (p.userData.character || ctx.characters.includes(p)) return true;
    return false;
  };
  const nearCasters: THREE.Object3D[] = [];
  if (Q.shadowCasters === "characters")
    scene.traverse((o) => {
      if (o.userData.castNear) nearCasters.push(o);
      if (o.castShadow && !(o as THREE.Light).isLight && !isCharacter(o)) {
        o.castShadow = false;
        applied.castersOff++;
      }
    });

  // post: bloom full, half or off; with bloom off and nothing else between, drop the output pass so the scene
  // renders straight to the canvas (three tone-maps and encodes in the material then: the same image, no targets)
  if (composer) {
    composer.setPixelRatio(pr);
    const passes = composer.passes;
    const blooms = passes.filter((p): p is UnrealBloomPass => p instanceof UnrealBloomPass);
    for (const b of blooms) {
      if (Q.bloom === "off") b.enabled = false;
      else if (Q.bloom === "half") {
        const full = b.setSize.bind(b);
        b.setSize = (w: number, h: number) => full(Math.max(1, Math.round(w / 2)), Math.max(1, Math.round(h / 2)));
        // a coarser chain spreads each mip twice as far: radius 0 (the fine mips weighted up) keeps the glow's size
        // close to high's (mean abs difference 7.6 against 33 at the default radius, measured on the spike's centre view)
        b.radius = 0;
      }
    }
    if (Q.bloom === "half") composer.setSize(innerWidth, innerHeight);
    const live = passes.filter((p) => p.enabled && !(p instanceof RenderPass));
    if (live.length > 0 && live.every((p) => p instanceof OutputPass)) {
      for (const p of live) p.enabled = false;
      applied.outputPass = false;
    }
    applied.bloomPasses = blooms.filter((b) => b.enabled).length;
  }

  // per frame, before the scene is culled: objects added since boot get the same caster and texture rules; far
  // outline hulls hide; near props cast when close. At `high` there is nothing to do.
  if (Q.outlineFar === Infinity && Q.shadowCasters === "all" && Q.maxTextureSize === Infinity) return;
  const prev = scene.onBeforeRender;
  const seen = new WeakSet<THREE.Object3D>();
  scene.traverse((o) => seen.add(o));
  const hidden = new Set<THREE.Object3D>();
  const wp = new THREE.Vector3();
  scene.onBeforeRender = function (r, s, cam, target, ...rest) {
    applied.outlinesHidden = 0;
    scene.traverse((o) => {
      if (!seen.has(o)) {
        seen.add(o);
        if (o.userData.castNear) nearCasters.push(o);
        else if (Q.shadowCasters === "characters" && o.castShadow && !(o as THREE.Light).isLight && !isCharacter(o)) {
          o.castShadow = false;
          applied.castersOff++;
        }
        const mats = (o as THREE.Mesh).material;
        if (mats && Q.maxTextureSize !== Infinity)
          for (const m of Array.isArray(mats) ? mats : [mats])
            for (const v of Object.values(m as unknown as Record<string, unknown>)) if ((v as THREE.Texture)?.isTexture) capTexture(v as THREE.Texture, Q.maxTextureSize, applied);
      }
      if (Q.outlineFar === Infinity || !o.name.endsWith("-outline")) return;
      const far = o.getWorldPosition(wp).distanceTo(cam.position) > Q.outlineFar;
      if (far && o.visible) {
        o.visible = false;
        hidden.add(o);
      } else if (!far && hidden.has(o)) {
        o.visible = true;
        hidden.delete(o);
      }
      if (hidden.has(o)) applied.outlinesHidden++;
    });
    for (const o of nearCasters) o.castShadow = o.getWorldPosition(wp).distanceTo(cam.position) < Q.nearCasterDist;
    (prev as (...a: unknown[]) => void).call(this, r, s, cam, target, ...rest);
  };
}
